/**
 * Card Benefits Calculator
 *
 * Calculate ROI on CRO Card cashback and benefits based on monthly spending
 */

import { useState, useMemo } from 'react';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';
import type { CROCard } from '../../lib/api/cryptocom-api';

interface CardBenefitsCalculatorProps {
  currentCard?: CROCard | null;
}

interface CardTier {
  name: string;
  tier: CROCard['tier'];
  requiredStake: number;
  stakingAPR: number;
  cashbackRate: number;
  color: string;
  benefits: {
    spotify?: number;
    netflix?: number;
    amazonPrime?: number;
    loungeAccess?: boolean;
  };
}

const CARD_TIERS: CardTier[] = [
  {
    name: 'Midnight Blue',
    tier: 'MIDNIGHT_BLUE',
    requiredStake: 0,
    stakingAPR: 0,
    cashbackRate: 1,
    color: '#1e3a8a',
    benefits: {}
  },
  {
    name: 'Ruby Steel',
    tier: 'RUBY_STEEL',
    requiredStake: 400,
    stakingAPR: 4,
    cashbackRate: 2,
    color: '#dc2626',
    benefits: {
      spotify: 13.99
    }
  },
  {
    name: 'Royal Indigo',
    tier: 'ROYAL_INDIGO',
    requiredStake: 4000,
    stakingAPR: 6,
    cashbackRate: 3,
    color: '#7c3aed',
    benefits: {
      spotify: 13.99,
      netflix: 15.99
    }
  },
  {
    name: 'Icy White',
    tier: 'ICY_WHITE',
    requiredStake: 40000,
    stakingAPR: 8,
    cashbackRate: 5,
    color: '#d1d5db',
    benefits: {
      spotify: 13.99,
      netflix: 15.99,
      amazonPrime: 14.99,
      loungeAccess: true
    }
  },
  {
    name: 'Obsidian',
    tier: 'OBSIDIAN',
    requiredStake: 400000,
    stakingAPR: 8,
    cashbackRate: 8,
    color: '#111827',
    benefits: {
      spotify: 13.99,
      netflix: 15.99,
      amazonPrime: 14.99,
      loungeAccess: true
    }
  }
];

export function CardBenefitsCalculator({ currentCard }: CardBenefitsCalculatorProps) {
  const [monthlySpending, setMonthlySpending] = useState(2000);
  const [croPrice, setCroPrice] = useState(0.10); // Default CRO price in USD
  const [showComparison, setShowComparison] = useState(false);

  // Calculate benefits for each tier
  const tierCalculations = useMemo(() => {
    return CARD_TIERS.map(tier => {
      // Monthly cashback
      const monthlyCashback = (monthlySpending * tier.cashbackRate) / 100;
      const annualCashback = monthlyCashback * 12;

      // Subscription benefits value
      const monthlyBenefitsValue =
        (tier.benefits.spotify || 0) +
        (tier.benefits.netflix || 0) +
        (tier.benefits.amazonPrime || 0);
      const annualBenefitsValue = monthlyBenefitsValue * 12;

      // Staking rewards
      const stakingValue = tier.requiredStake * croPrice;
      const annualStakingRewards = (stakingValue * tier.stakingAPR) / 100;

      // Total annual value
      const totalAnnualValue = annualCashback + annualBenefitsValue + annualStakingRewards;

      // Payback period (how long to recover stake)
      const paybackMonths = stakingValue > 0
        ? stakingValue / (monthlyCashback + monthlyBenefitsValue + (annualStakingRewards / 12))
        : 0;

      // ROI percentage
      const roi = stakingValue > 0 ? (totalAnnualValue / stakingValue) * 100 : 0;

      return {
        tier,
        monthlyCashback,
        annualCashback,
        monthlyBenefitsValue,
        annualBenefitsValue,
        annualStakingRewards,
        totalAnnualValue,
        stakingValue,
        paybackMonths,
        roi
      };
    });
  }, [monthlySpending, croPrice]);

  // Find optimal tier (best ROI)
  const optimalTier = useMemo(() => {
    return tierCalculations.reduce((best, current) => {
      if (current.roi > best.roi) return current;
      return best;
    }, tierCalculations[0]);
  }, [tierCalculations]);

  // Current tier calculation
  const currentTierCalc = currentCard
    ? tierCalculations.find(t => t.tier.tier === currentCard.tier)
    : tierCalculations[0];

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.CreditCard size={24} />
          Calculateur de Bénéfices CRO Card
        </h2>
        <button
          onClick={() => setShowComparison(!showComparison)}
          style={{
            padding: '8px 16px',
            background: showComparison ? 'var(--accent)' : 'transparent',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            color: showComparison ? 'white' : 'var(--text)',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <Icons.BarChart size={16} />
          {showComparison ? 'Masquer' : 'Comparer'} les tiers
        </button>
      </div>

      {/* Input Parameters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px',
        background: 'var(--bg)',
        borderRadius: '8px'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: 500,
            marginBottom: '8px',
            color: 'var(--muted)'
          }}>
            Dépenses mensuelles ($)
          </label>
          <input
            type="number"
            value={monthlySpending}
            onChange={(e) => setMonthlySpending(Number(e.target.value))}
            min="0"
            step="100"
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text)',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: 500,
            marginBottom: '8px',
            color: 'var(--muted)'
          }}>
            Prix CRO actuel ($)
          </label>
          <input
            type="number"
            value={croPrice}
            onChange={(e) => setCroPrice(Number(e.target.value))}
            min="0"
            step="0.01"
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text)',
              fontSize: '14px'
            }}
          />
        </div>
      </div>

      {/* Current Tier Summary (if available) */}
      {currentCard && currentTierCalc && (
        <div style={{
          padding: '20px',
          background: `linear-gradient(135deg, ${currentTierCalc.tier.color}20 0%, ${currentTierCalc.tier.color}10 100%)`,
          borderRadius: '8px',
          border: `2px solid ${currentTierCalc.tier.color}`,
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <h3 style={{ margin: 0, marginBottom: '4px', fontSize: '18px' }}>
                Votre carte actuelle: {currentTierCalc.tier.name}
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)' }}>
                {currentCard.cashbackRate}% cashback • {fmt.currency(currentCard.stakedAmount)} CRO staké
              </p>
            </div>
            <div style={{
              padding: '8px 16px',
              background: currentTierCalc.roi > 0 ? '#10b981' : 'var(--muted)',
              borderRadius: '6px',
              color: 'white',
              fontWeight: 600
            }}>
              ROI: {currentTierCalc.roi.toFixed(1)}%
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px'
          }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                Cashback mensuel
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#10b981' }}>
                {fmt.currency(currentTierCalc.monthlyCashback)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                Avantages mensuels
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#8b5cf6' }}>
                {fmt.currency(currentTierCalc.monthlyBenefitsValue)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                Valeur annuelle totale
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#3b82f6' }}>
                {fmt.currency(currentTierCalc.totalAnnualValue)}
              </div>
            </div>
            {currentTierCalc.paybackMonths > 0 && (
              <div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                  Récupération stake
                </div>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>
                  {currentTierCalc.paybackMonths.toFixed(1)} mois
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Optimal Tier Recommendation */}
      {optimalTier && optimalTier.tier.tier !== currentCard?.tier && (
        <div style={{
          padding: '16px',
          background: 'rgba(59, 130, 246, 0.1)',
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Icons.TrendingUp size={24} style={{ color: '#3b82f6', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
              💡 Recommandation: {optimalTier.tier.name}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
              Meilleur ROI de {optimalTier.roi.toFixed(1)}% avec vos dépenses mensuelles de {fmt.currency(monthlySpending)}.
              Récupération du stake en {optimalTier.paybackMonths.toFixed(1)} mois.
            </div>
          </div>
        </div>
      )}

      {/* Tier Comparison Table */}
      {showComparison && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px'
          }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Tier</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Stake CRO</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Cashback</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Mensuel</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Annuel</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>ROI</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Récup.</th>
              </tr>
            </thead>
            <tbody>
              {tierCalculations.map((calc, idx) => (
                <tr
                  key={calc.tier.tier}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: calc.tier.tier === optimalTier.tier.tier
                      ? 'rgba(59, 130, 246, 0.05)'
                      : calc.tier.tier === currentCard?.tier
                      ? 'rgba(16, 185, 129, 0.05)'
                      : 'transparent'
                  }}
                >
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '2px',
                          background: calc.tier.color
                        }}
                      />
                      <span style={{ fontWeight: 500 }}>{calc.tier.name}</span>
                      {calc.tier.tier === currentCard?.tier && (
                        <span style={{
                          padding: '2px 6px',
                          background: '#10b981',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600
                        }}>
                          ACTUELLE
                        </span>
                      )}
                      {calc.tier.tier === optimalTier.tier.tier && (
                        <span style={{
                          padding: '2px 6px',
                          background: '#3b82f6',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 600
                        }}>
                          OPTIMAL
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: 'var(--muted)' }}>
                    {calc.tier.requiredStake.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                    {calc.tier.cashbackRate}%
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#10b981' }}>
                    {fmt.currency(calc.monthlyCashback + calc.monthlyBenefitsValue)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#3b82f6', fontWeight: 600 }}>
                    {fmt.currency(calc.totalAnnualValue)}
                  </td>
                  <td style={{
                    padding: '12px',
                    textAlign: 'right',
                    fontWeight: 600,
                    color: calc.roi > 20 ? '#10b981' : calc.roi > 10 ? '#f59e0b' : 'var(--muted)'
                  }}>
                    {calc.roi.toFixed(1)}%
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: 'var(--muted)' }}>
                    {calc.paybackMonths > 0 ? `${calc.paybackMonths.toFixed(1)}m` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Benefits Breakdown */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>
          Avantages inclus par tier
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '12px'
        }}>
          {CARD_TIERS.slice(1).map(tier => (
            <div
              key={tier.tier}
              style={{
                padding: '12px',
                background: 'var(--bg)',
                borderRadius: '6px',
                border: `2px solid ${tier.tier === currentCard?.tier ? tier.color : 'var(--border)'}`
              }}
            >
              <div style={{
                fontWeight: 600,
                marginBottom: '8px',
                color: tier.color
              }}>
                {tier.name}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {tier.benefits.spotify && (
                  <div style={{ marginBottom: '4px' }}>✓ Spotify ({fmt.currency(tier.benefits.spotify)}/mois)</div>
                )}
                {tier.benefits.netflix && (
                  <div style={{ marginBottom: '4px' }}>✓ Netflix ({fmt.currency(tier.benefits.netflix)}/mois)</div>
                )}
                {tier.benefits.amazonPrime && (
                  <div style={{ marginBottom: '4px' }}>✓ Amazon Prime ({fmt.currency(tier.benefits.amazonPrime)}/mois)</div>
                )}
                {tier.benefits.loungeAccess && (
                  <div style={{ marginBottom: '4px' }}>✓ Lounge Access</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        marginTop: '24px',
        padding: '12px',
        background: 'rgba(245, 158, 11, 0.1)',
        borderRadius: '6px',
        fontSize: '12px',
        color: 'var(--muted)',
        lineHeight: '1.6'
      }}>
        <strong>Note:</strong> Les calculs sont basés sur le prix CRO actuel et les dépenses mensuelles indiquées.
        Les avantages réels peuvent varier selon votre région et les conditions de Crypto.com.
      </div>
    </div>
  );
}
